import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import Button from '@/components/Button';

/**
 * React error boundary. The error UI reuses the portal's page-header
 * typography (`page-title`) and the `.dashboard-section` card surface so a
 * crashed page still looks like the original portal.
 */

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (err: Error, retry: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface in dev console; feature units can wire this to Sentry later.
    console.error('ErrorBoundary caught:', error, info);
  }

  retry = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.retry);

    return (
      <div
        role="alert"
        // Card surface: white bg, 8px radius, soft shadow.
        className="mx-auto my-16 max-w-xl bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.1)] p-8"
      >
        <h2 className="font-[var(--font-heading)] text-[1.75rem] font-bold text-[color:var(--text-dark)] m-0">
          Something went wrong
        </h2>
        <p className="mt-3 text-sm text-[color:var(--text-dark)] opacity-80 break-words">
          {error.message}
        </p>
        <div className="mt-5">
          <Button variant="primary" size="sm" onClick={this.retry}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
}
